// First-person weapon + hands, drawn in screen space (logical 1280x720) over
// the raycast world. The hero is Alan Turing: 1940s rolled-up shirtsleeves,
// and — because the support wrist is visible — a stylized Bremont Codebreaker
// on the LEFT wrist (aviation chronograph: steel case, cream dial, twin
// subdials, brown leather strap, Bletchley-linked crown). The watch is drawn
// last so the gun never occludes it.
//
// Only the passed 2D context is touched here (no offscreen canvases), so the
// class is DOM-free and safe to construct under the headless test runner.

// Skin + cloth palette (kept consistent with the HUD face).
const SKIN = "#e7b48c";
const SKIN_DK = "#c6875f";
const SKIN_LT = "#f4cda8";
const SLEEVE = "#e9e0cb";
const SLEEVE_DK = "#c8bda2";
const SLEEVE_SH = "#b7ab8c";

// Anchor geometry (logical coords). Left = support hand on the handguard;
// right = trigger hand on the grip. Muzzle sits just below screen centre.
const MUZZLE = { x: 560, y: 452 };
const GRIP = { x: 726, y: 590 };
const L_HAND = { x: 610, y: 496 };
const R_HAND = { x: 720, y: 588 };
const L_BASE = { x: 384, y: 733 };
const R_BASE = { x: 906, y: 733 };

export interface WeaponDrawOptions {
  readonly moving: boolean;
  readonly reducedMotion: boolean;
}

export class Weapon {
  readonly magazine = 12;
  ammoInMag = 12;
  private cooldown = 0;
  private recoil = 0; // 0..1, decays
  private flash = 0; // 0..1, decays
  private reloadTimer = 0;
  private bobPhase = 0;
  private readonly fireInterval = 0.14;
  private readonly reloadTime = 1.15;

  get reloading(): boolean {
    return this.reloadTimer > 0;
  }

  get flashLevel(): number {
    return this.flash;
  }

  /** Muzzle position in logical coords (for spawning tracer/flash light). */
  get muzzle(): { x: number; y: number } {
    return { x: MUZZLE.x, y: MUZZLE.y };
  }

  update(dt: number, moving: boolean): void {
    if (this.cooldown > 0) this.cooldown = Math.max(0, this.cooldown - dt);
    this.recoil = Math.max(0, this.recoil - dt * 4.6);
    this.flash = Math.max(0, this.flash - dt * 12);
    if (this.reloadTimer > 0) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        this.reloadTimer = 0;
        this.ammoInMag = this.magazine;
      }
    }
    this.bobPhase += dt * (moving ? 9 : 3.1);
  }

  startReload(): void {
    if (!this.reloading && this.ammoInMag < this.magazine) this.reloadTimer = this.reloadTime;
  }

  /** Returns true if a round left the barrel this call. */
  tryFire(): boolean {
    if (this.reloading || this.cooldown > 0) return false;
    if (this.ammoInMag <= 0) {
      this.startReload();
      return false;
    }
    this.ammoInMag -= 1;
    this.cooldown = this.fireInterval;
    this.recoil = 1;
    this.flash = 1;
    if (this.ammoInMag === 0) this.startReload();
    return true;
  }

  draw(ctx: CanvasRenderingContext2D, opts: WeaponDrawOptions): void {
    const bob = opts.reducedMotion ? 0 : 1;
    const bobX = Math.sin(this.bobPhase) * 9 * bob;
    const bobY = Math.abs(Math.sin(this.bobPhase)) * 7 * bob;
    const kick = this.recoil * 22;
    const reloadDip = this.reloading ? Math.sin(Math.min(1, 1 - this.reloadTimer / this.reloadTime) * Math.PI) * 60 : 0;

    ctx.save();
    ctx.translate(bobX, bobY + kick + reloadDip);

    drawRightArm(ctx);
    drawRifle(ctx, this.recoil);
    drawLeftArm(ctx); // over the handguard — carries the watch, always visible
    if (this.flash > 0) drawMuzzleFlash(ctx, MUZZLE.x, MUZZLE.y - kick * 0.4, this.flash);

    ctx.restore();
  }
}

// ---------------------------------------------------------------------------
// Arms
// ---------------------------------------------------------------------------

/** Tapered forearm from a wide base (screen bottom) up to the wrist, with a
 *  rolled shirt-sleeve cuff at the base. `flip` mirrors shading for the right arm. */
function drawForearm(
  ctx: CanvasRenderingContext2D,
  base: { x: number; y: number },
  wrist: { x: number; y: number },
  baseHalf: number,
  wristHalf: number,
): { nx: number; ny: number; angle: number } {
  const dx = wrist.x - base.x;
  const dy = wrist.y - base.y;
  const len = Math.hypot(dx, dy) || 1;
  const ax = dx / len;
  const ay = dy / len;
  const nx = -ay; // unit normal
  const ny = ax;
  const angle = Math.atan2(dy, dx);

  // Forearm body.
  ctx.beginPath();
  ctx.moveTo(base.x + nx * baseHalf, base.y + ny * baseHalf);
  ctx.lineTo(wrist.x + nx * wristHalf, wrist.y + ny * wristHalf);
  ctx.lineTo(wrist.x - nx * wristHalf, wrist.y - ny * wristHalf);
  ctx.lineTo(base.x - nx * baseHalf, base.y - ny * baseHalf);
  ctx.closePath();
  const grad = ctx.createLinearGradient(
    base.x - nx * baseHalf,
    base.y - ny * baseHalf,
    base.x + nx * baseHalf,
    base.y + ny * baseHalf,
  );
  grad.addColorStop(0, SKIN_DK);
  grad.addColorStop(0.45, SKIN);
  grad.addColorStop(1, SKIN_LT);
  ctx.fillStyle = grad;
  ctx.fill();

  // Rolled sleeve cuff at the base.
  const cuffMid = 0.24;
  const cx0 = base.x + ax * (len * 0) ;
  const cy0 = base.y + ay * (len * 0);
  const cx1 = base.x + ax * (len * cuffMid);
  const cy1 = base.y + ay * (len * cuffMid);
  const cuffHalf0 = baseHalf + 10;
  const cuffHalf1 = baseHalf * 0.9 + wristHalf * 0.1 + 6;
  ctx.beginPath();
  ctx.moveTo(cx0 + nx * cuffHalf0, cy0 + ny * cuffHalf0);
  ctx.lineTo(cx1 + nx * cuffHalf1, cy1 + ny * cuffHalf1);
  ctx.lineTo(cx1 - nx * cuffHalf1, cy1 - ny * cuffHalf1);
  ctx.lineTo(cx0 - nx * cuffHalf0, cy0 - ny * cuffHalf0);
  ctx.closePath();
  const cuffGrad = ctx.createLinearGradient(
    cx0 - nx * cuffHalf0,
    cy0 - ny * cuffHalf0,
    cx0 + nx * cuffHalf0,
    cy0 + ny * cuffHalf0,
  );
  cuffGrad.addColorStop(0, SLEEVE_SH);
  cuffGrad.addColorStop(0.5, SLEEVE);
  cuffGrad.addColorStop(1, SLEEVE_DK);
  ctx.fillStyle = cuffGrad;
  ctx.fill();
  // Cuff fold line.
  ctx.strokeStyle = SLEEVE_SH;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx1 + nx * cuffHalf1, cy1 + ny * cuffHalf1);
  ctx.lineTo(cx1 - nx * cuffHalf1, cy1 - ny * cuffHalf1);
  ctx.stroke();

  return { nx, ny, angle };
}

function drawHand(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  // Palm / fist.
  ctx.fillStyle = SKIN;
  roundRect(ctx, -22, -20, 44, 40, 13);
  ctx.fill();
  // Knuckle highlights.
  ctx.fillStyle = SKIN_LT;
  for (let i = 0; i < 4; i++) {
    roundRect(ctx, -20 + i * 11, -20, 9, 12, 4);
    ctx.fill();
  }
  // Thumb.
  ctx.fillStyle = SKIN_DK;
  roundRect(ctx, 6, 8, 20, 12, 6);
  ctx.fill();
  ctx.restore();
}

function drawLeftArm(ctx: CanvasRenderingContext2D): void {
  const { nx, ny, angle } = drawForearm(ctx, L_BASE, L_HAND, 74, 40);
  // Watch sits on the wrist, a little below the hand along the forearm.
  const t = 0.62;
  const wx = L_BASE.x + (L_HAND.x - L_BASE.x) * t;
  const wy = L_BASE.y + (L_HAND.y - L_BASE.y) * t;
  drawBremontCodebreaker(ctx, wx, wy, angle);
  drawHand(ctx, L_HAND.x, L_HAND.y, angle + Math.PI / 2);
  void nx;
  void ny;
}

function drawRightArm(ctx: CanvasRenderingContext2D): void {
  drawForearm(ctx, R_BASE, R_HAND, 78, 42);
  drawHand(ctx, R_HAND.x, R_HAND.y, Math.atan2(R_HAND.y - R_BASE.y, R_HAND.x - R_BASE.x) - Math.PI / 2);
}

// ---------------------------------------------------------------------------
// Rifle
// ---------------------------------------------------------------------------

function drawRifle(ctx: CanvasRenderingContext2D, recoil: number): void {
  const dx = MUZZLE.x - GRIP.x;
  const dy = MUZZLE.y - GRIP.y;
  const len = Math.hypot(dx, dy);
  const angle = Math.atan2(dy, dx);

  ctx.save();
  ctx.translate(GRIP.x, GRIP.y);
  ctx.rotate(angle);
  // Now +x points toward the muzzle; +y is "down-right" across the gun.

  const barrelEnd = len + 26;

  // Wooden stock behind the grip.
  ctx.fillStyle = "#5a3a1e";
  roundRect(ctx, -104, -6, 82, 30, 8);
  ctx.fill();
  ctx.fillStyle = "#6d492a";
  roundRect(ctx, -104, -6, 82, 12, 8);
  ctx.fill();

  // Receiver (dark metal body).
  const body = ctx.createLinearGradient(0, -20, 0, 22);
  body.addColorStop(0, "#3a4048");
  body.addColorStop(0.5, "#22262c");
  body.addColorStop(1, "#12151a");
  ctx.fillStyle = body;
  roundRect(ctx, -34, -19, 104, 40, 9);
  ctx.fill();

  // Ejection port + charging line.
  ctx.strokeStyle = "#454c55";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-18, -12);
  ctx.lineTo(52, -12);
  ctx.stroke();
  ctx.fillStyle = "#0c0e12";
  roundRect(ctx, 6, -10, 26, 9, 3);
  ctx.fill();

  // Curved magazine dropping below the receiver.
  ctx.save();
  ctx.translate(2, 20);
  ctx.rotate(0.12);
  ctx.fillStyle = "#181c22";
  roundRect(ctx, -12, 0, 26, 54, 7);
  ctx.fill();
  ctx.fillStyle = "#2a3038";
  roundRect(ctx, -12, 0, 26, 10, 6);
  ctx.fill();
  ctx.restore();

  // Handguard + barrel toward the muzzle.
  const guard = ctx.createLinearGradient(0, -12, 0, 12);
  guard.addColorStop(0, "#2c313a");
  guard.addColorStop(1, "#0f1216");
  ctx.fillStyle = guard;
  roundRect(ctx, 66, -12, len - 40, 24, 8);
  ctx.fill();
  // Vent slots on the handguard.
  ctx.fillStyle = "#05070a";
  for (let x = 84; x < len - 10; x += 16) {
    roundRect(ctx, x, -6, 8, 12, 3);
    ctx.fill();
  }

  // Barrel + front sight + muzzle.
  ctx.fillStyle = "#0b0d11";
  roundRect(ctx, len - 30, -7, 56, 14, 5);
  ctx.fill();
  ctx.fillStyle = "#3a4048";
  roundRect(ctx, len - 6, -13, 7, 16, 2); // front sight post
  ctx.fill();
  ctx.fillStyle = "#05070a";
  ctx.beginPath();
  ctx.arc(barrelEnd, 0, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#101418";
  ctx.beginPath();
  ctx.arc(barrelEnd, 0, 4.5, 0, Math.PI * 2);
  ctx.fill();

  // Hot barrel tint right after firing.
  if (recoil > 0.2) {
    ctx.fillStyle = `rgba(255,140,60,${(recoil - 0.2) * 0.5})`;
    roundRect(ctx, len - 30, -7, 56, 14, 5);
    ctx.fill();
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Bremont Codebreaker (stylized). Local +x follows the forearm toward the hand,
// which is treated as the 12 o'clock direction.
// ---------------------------------------------------------------------------

function drawBremontCodebreaker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  forearmAngle: number,
): void {
  const R = 31; // case radius (~62px case — deliberately readable)
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(forearmAngle); // +x -> toward hand (12 o'clock)

  // Soft contact shadow on the wrist.
  ctx.fillStyle = "rgba(0,0,0,0.28)";
  ctx.beginPath();
  ctx.ellipse(3, 6, R + 10, R - 4, 0, 0, Math.PI * 2);
  ctx.fill();

  // Leather strap running along the forearm (±x), passing under the case.
  const strapHalf = 26;
  drawStrapSegment(ctx, R - 4, R + 34, strapHalf, strapHalf - 3);
  drawStrapSegment(ctx, -(R + 34), -(R - 4), strapHalf - 3, strapHalf);

  // Steel case: outer ring with a brushed radial sheen.
  const caseGrad = ctx.createLinearGradient(0, -R, 0, R);
  caseGrad.addColorStop(0, "#eef1f4");
  caseGrad.addColorStop(0.5, "#aab0b8");
  caseGrad.addColorStop(1, "#6f757d");
  ctx.fillStyle = caseGrad;
  ctx.beginPath();
  ctx.arc(0, 0, R, 0, Math.PI * 2);
  ctx.fill();
  // Lugs (four) anchoring the strap.
  ctx.fillStyle = "#7c828a";
  for (const lug of [
    [R - 6, -strapHalf + 4],
    [R - 6, strapHalf - 4],
    [-(R - 6), -strapHalf + 4],
    [-(R - 6), strapHalf - 4],
  ]) {
    roundRect(ctx, lug[0]! - 4, lug[1]! - 4, 8, 8, 2);
    ctx.fill();
  }

  // Crown + two chronograph pushers on the case side (3 o'clock = +y here).
  ctx.fillStyle = "#5c6268";
  roundRect(ctx, -5, R - 2, 10, 8, 2); // crown
  ctx.fill();
  ctx.fillStyle = "#7c828a";
  roundRect(ctx, -14, R - 1, 6, 5, 2); // pusher
  ctx.fill();
  roundRect(ctx, 9, R - 1, 6, 5, 2); // pusher
  ctx.fill();

  // Inner bezel ring.
  ctx.strokeStyle = "#5b616a";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(0, 0, R - 4, 0, Math.PI * 2);
  ctx.stroke();

  // Cream dial.
  const dialR = R - 8;
  const dial = ctx.createRadialGradient(-4, -4, 2, 0, 0, dialR);
  dial.addColorStop(0, "#f6efdc");
  dial.addColorStop(1, "#e4d8bc");
  ctx.fillStyle = dial;
  ctx.beginPath();
  ctx.arc(0, 0, dialR, 0, Math.PI * 2);
  ctx.fill();

  // Minute track + hour ticks (12 = +x). Draw in dial space.
  ctx.strokeStyle = "#2b2a24";
  ctx.lineWidth = 1;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const ox = Math.cos(a);
    const oy = Math.sin(a);
    ctx.beginPath();
    ctx.moveTo(ox * (dialR - 1), oy * (dialR - 1));
    ctx.lineTo(ox * (dialR - 5), oy * (dialR - 5));
    ctx.lineWidth = i % 3 === 0 ? 2.4 : 1;
    ctx.stroke();
  }

  // Twin chronograph subdials at 9 (-y) and 3 (+y) o'clock.
  drawSubdial(ctx, 0, -dialR * 0.44, dialR * 0.3);
  drawSubdial(ctx, 0, dialR * 0.44, dialR * 0.3);
  // Date window at 6 o'clock (toward the elbow, -x).
  ctx.fillStyle = "#fbf7ec";
  roundRect(ctx, -dialR * 0.66 - 5, -6, 12, 12, 2);
  ctx.fill();
  ctx.strokeStyle = "#8a826d";
  ctx.lineWidth = 1;
  roundRect(ctx, -dialR * 0.66 - 5, -6, 12, 12, 2);
  ctx.stroke();

  // Hands: blued hour + minute, red central chrono seconds. Fixed 10:09 pose.
  drawHand2(ctx, -0.62, dialR * 0.5, 4, "#20304f"); // hour
  drawHand2(ctx, 1.98, dialR * 0.74, 3, "#20304f"); // minute
  drawHand2(ctx, 2.7, dialR * 0.82, 1.6, "#d23b2f"); // chrono seconds
  ctx.fillStyle = "#20304f";
  ctx.beginPath();
  ctx.arc(0, 0, 2.6, 0, Math.PI * 2);
  ctx.fill();

  // Domed crystal glare.
  ctx.fillStyle = "rgba(255,255,255,0.16)";
  ctx.beginPath();
  ctx.ellipse(-dialR * 0.3, -dialR * 0.35, dialR * 0.55, dialR * 0.32, -0.7, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawStrapSegment(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  half0: number,
  half1: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x0, -half0);
  ctx.lineTo(x1, -half1);
  ctx.lineTo(x1, half1);
  ctx.lineTo(x0, half0);
  ctx.closePath();
  const g = ctx.createLinearGradient(0, -half0, 0, half0);
  g.addColorStop(0, "#6b4326");
  g.addColorStop(0.5, "#4f2f18");
  g.addColorStop(1, "#38200f");
  ctx.fillStyle = g;
  ctx.fill();
  // Contrast stitching.
  ctx.strokeStyle = "#caa26a";
  ctx.lineWidth = 1.4;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.moveTo(x0, -half0 + 5);
  ctx.lineTo(x1, -half1 + 5);
  ctx.moveTo(x0, half0 - 5);
  ctx.lineTo(x1, half1 - 5);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawSubdial(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.save();
  ctx.translate(x, y);
  const g = ctx.createRadialGradient(-1, -1, 1, 0, 0, r);
  g.addColorStop(0, "#ded0b0");
  g.addColorStop(1, "#c8b78f");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#8a826d";
  ctx.lineWidth = 1;
  ctx.stroke();
  // Little hand.
  ctx.strokeStyle = "#2b2a24";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(Math.cos(-0.4) * r * 0.7, Math.sin(-0.4) * r * 0.7);
  ctx.stroke();
  ctx.restore();
}

/** Draw a watch hand from the centre at `angle` (0 = +x = 12 o'clock). */
function drawHand2(
  ctx: CanvasRenderingContext2D,
  angle: number,
  length: number,
  width: number,
  color: string,
): void {
  ctx.save();
  ctx.rotate(angle);
  ctx.fillStyle = color;
  roundRect(ctx, -3, -width / 2, length + 3, width, width / 2);
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Muzzle flash
// ---------------------------------------------------------------------------

function drawMuzzleFlash(ctx: CanvasRenderingContext2D, x: number, y: number, intensity: number): void {
  ctx.save();
  ctx.translate(x, y);
  const r = 26 + intensity * 46;
  const glow = ctx.createRadialGradient(0, 0, 2, 0, 0, r);
  glow.addColorStop(0, `rgba(255,246,214,${0.95 * intensity})`);
  glow.addColorStop(0.4, `rgba(255,168,64,${0.7 * intensity})`);
  glow.addColorStop(1, "rgba(255,120,40,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  // Star spikes.
  ctx.fillStyle = `rgba(255,236,190,${intensity})`;
  ctx.rotate(intensity * 1.7);
  for (let i = 0; i < 5; i++) {
    ctx.rotate((Math.PI * 2) / 5);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(6, -3);
    ctx.lineTo(r * 0.92, 0);
    ctx.lineTo(6, 3);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
